import {
  BUILT_IN_CHASSIS_CODE_LISTS,
  CHASSIS_CODE_LIST_IDS,
  CHASSIS_CODE_LISTS,
  type ChassisCodeListId,
  squashChassisCode,
} from "./reference-data.ts";
import type { DataLanguage } from "./vocabularies.ts";

/**
 * BMW's own variant code without the model in brackets ("E36/2S" for "E36/2S (M3)"), where that
 * bare code isn't a list code of its own (unlike "E92" next to "E92 (M3)") and only one entry
 * carries it: "K50" stays a code of its own, since it could be "K50 (R1200GS)" or "K50 (R1250GS)".
 */
const ALIASES = (() => {
  const entries = CHASSIS_CODE_LIST_IDS.flatMap((list) => CHASSIS_CODE_LISTS[list]);
  const codes = new Set(entries.map((e) => squashChassisCode(e.code)));
  const targets = new Map<string, string[]>();
  const aliased = CHASSIS_CODE_LIST_IDS.filter((list) => BUILT_IN_CHASSIS_CODE_LISTS[list].aliases);
  for (const e of aliased.flatMap((list) => CHASSIS_CODE_LISTS[list])) {
    const bare = squashChassisCode(e.code.replace(/\s*\(.*\)$/, ""));
    if (bare === squashChassisCode(e.code) || codes.has(bare)) continue;
    targets.set(bare, [...(targets.get(bare) ?? []), squashChassisCode(e.code)]);
  }
  return new Map(
    [...targets].flatMap(([bare, [target, ...others]]) =>
      target && others.length === 0 ? [[bare, target] as const] : [],
    ),
  );
})();

/**
 * The form two spellings of one chassis code share: case, spaces, hyphens and brackets don't
 * count ("e 90" is E90; "E92-M3" and "E92 M3" are "E92 (M3)"), and BMW's bare variant code is its
 * bracketed entry ("E36/2S" is "E36/2S (M3)").
 */
export function normalizeChassisCode(code: string): string {
  const squashed = squashChassisCode(code);
  return ALIASES.get(squashed) ?? squashed;
}

/** Natural order, so E9 comes before E10 and E36 before E36/7. */
export function compareChassisCodes(a: string, b: string): number {
  return a.localeCompare(b, "en", { numeric: true, sensitivity: "base" });
}

/** Model names of a code across the built-in lists ("3 Series", "M3"), empty for own codes. */
export function chassisCodeModels(code: string, language: DataLanguage): string[] {
  const key = normalizeChassisCode(code);
  const models = CHASSIS_CODE_LIST_IDS.flatMap((list) =>
    CHASSIS_CODE_LISTS[list]
      .filter((e) => normalizeChassisCode(e.code) === key)
      .map((e) => e.model[language]),
  );
  return [...new Set(models)];
}

/** Body sub-code → its generation, from the built-in lists ("E46/2" → "E46"), normalized. */
const PARENTS = new Map(
  CHASSIS_CODE_LIST_IDS.flatMap((list) =>
    CHASSIS_CODE_LISTS[list].flatMap((e) =>
      e.parent ? [[normalizeChassisCode(e.code), normalizeChassisCode(e.parent)] as const] : [],
    ),
  ),
);

/**
 * A part or template fits a vehicle when one of its chassis codes is the vehicle's chassis code
 * or, for a body sub-code, its generation: an "E46" part fits an "E46/2" coupé, but an "E46/2"
 * part does not fit every "E46". Variants never inherit from the base model: an "E92" part does
 * not fit an "E92 (M3)", and an "E36" part does not fit an "E36/7" Z3. Codes are compared by
 * their fixed `key`, so renaming a chassis code's label never changes what fits. Returns the
 * matching code of the part or template, or null.
 */
export function fittingChassisCode<T extends { key: string }>(
  codes: readonly T[],
  vehicleKey: string | null | undefined,
): T | null {
  for (const target of fittingChassisKeys(vehicleKey)) {
    const exact = codes.find((code) => normalizeChassisCode(code.key) === target);
    if (exact) return exact;
  }
  return null;
}

/**
 * The normalized keys a part or template may carry to fit a vehicle, most specific first: the
 * vehicle's own code, then its generation ("E46/2" → ["E46/2", "E46"]).
 */
export function fittingChassisKeys(vehicleKey: string | null | undefined): string[] {
  const keys: string[] = [];
  let target = vehicleKey ? normalizeChassisCode(vehicleKey) : "";
  while (target && !keys.includes(target)) {
    keys.push(target);
    target = PARENTS.get(target) ?? "";
  }
  return keys;
}

/** A built-in chassis code found in free text. */
export interface ChassisCodeMatch {
  /** The code as its list writes it ("E90 (M3)"). */
  code: string;
  /** Its normalized key, what a garage's chassis code and a fit are compared by. */
  key: string;
  list: ChassisCodeListId;
  /** The words of the text it was found in ("E90"). */
  text: string;
}

interface Candidate {
  code: string;
  list: ChassisCodeListId;
  /** The bracketed model ("M3" of "E90 (M3)"), squashed; null for a plain code. */
  model: string | null;
}

/** Built-in codes by their code without the bracketed model, squashed: "E90" → E90, E90 (M3). */
const BY_BARE = (() => {
  const index = new Map<string, Candidate[]>();
  for (const list of CHASSIS_CODE_LIST_IDS) {
    for (const entry of CHASSIS_CODE_LISTS[list]) {
      const model = /\((.*)\)$/.exec(entry.code)?.[1] ?? null;
      const bare = squashChassisCode(entry.code.replace(/\s*\(.*\)$/, ""));
      const candidate = { code: entry.code, list, model: model && squashChassisCode(model) };
      index.set(bare, [...(index.get(bare) ?? []), candidate]);
    }
  }
  return index;
})();

/**
 * The built-in chassis codes named in free text, in the order they appear, each once:
 * "3er E90 LCI Limousine" → E90, "1er E81, E87" → E81 and E87. A code is a whole word (two for
 * "911 G"). Where a code has variants, the text decides: "E92 M3 Coupé" is "E92 (M3)", "E92 Coupé"
 * is "E92", BMW's own variant code "E36/2S" is "E36/2S (M3)", and "K50" alone is skipped, since
 * it could be "K50 (R1200GS)" or "K50 (R1250GS)".
 * Only the codes are read; a shop's own way of naming models ("Z3 E36" for the E36/7) is for its
 * reader to map. `lists` narrows the search, e.g. to a make package's lists.
 */
export function chassisCodesIn(
  text: string,
  lists: readonly ChassisCodeListId[] = CHASSIS_CODE_LIST_IDS,
): ChassisCodeMatch[] {
  const words = text.split(/[\s,;:|[\]()"„“”–—]+|\s-\s/).filter((w) => w.length > 0);
  const squashed = words.map(squashChassisCode);
  /** Whether the model appears as one to three neighbouring words ("X5 M", "R 1250 GS"). */
  const named = (model: string) =>
    squashed.some((_, i) =>
      [1, 2, 3].some(
        (n) => i + n <= squashed.length && squashed.slice(i, i + n).join("") === model,
      ),
    );
  const found = new Map<string, ChassisCodeMatch>();
  for (let i = 0; i < words.length; i++) {
    for (const n of [2, 1]) {
      if (i + n > words.length) continue;
      const phrase = words.slice(i, i + n).join(" ");
      const candidates = (BY_BARE.get(squashChassisCode(phrase)) ?? []).filter((c) =>
        lists.includes(c.list),
      );
      if (candidates.length === 0) continue;
      const variants = candidates.filter((c) => c.model !== null && named(c.model));
      const plain = candidates.filter((c) => c.model === null);
      // BMW's own variant code ("E36/2S") means its entry, as normalizeChassisCode reads it.
      const alias = candidates.filter(
        (c) => normalizeChassisCode(c.code) === normalizeChassisCode(phrase),
      );
      const chosen = variants.length > 0 ? variants : plain.length > 0 ? plain : alias;
      for (const c of chosen) {
        const key = normalizeChassisCode(c.code);
        if (!found.has(key)) found.set(key, { code: c.code, key, list: c.list, text: phrase });
      }
      i += n - 1;
      break;
    }
  }
  return [...found.values()];
}
